import { ref, computed, onMounted } from "vue";
import { auth } from "@/firebase";
import { pendingPostTestService, type PendingPostTest } from "@/services/pendingPostTestService";
import { logDebug, logInfo } from "@/utils/secureLogger";

export function usePendingPostTest() {
  const pendingPostTests = ref<PendingPostTest[]>([]);
  const isLoading = ref(false);

  // Computed: Get next pending post-test (most urgent)
  const nextPendingPostTest = computed(() => {
    return pendingPostTests.value.length > 0 ? pendingPostTests.value[0] : null;
  });

  // Computed: Count of pending tests
  const pendingCount = computed(() => pendingPostTests.value.length);

  // Computed: Check if any test is mandatory (deadline passed)
  const hasMandatory = computed(() => {
    return pendingPostTests.value.some((test) => test.isMandatory === true);
  });

  // Computed: Check if booking should be blocked (user has pending tests)
  // NOTE: This will be used to block NEW bookings only, not upcoming sessions
  const hasBlockingPendingPostTest = computed(() => {
    return pendingPostTests.value.length > 0;
  });

  /**
   * Check for pending post-tests for the current user
   * ✅ NOW INCLUDES: Automatic detection and re-creation of missing pending post-tests
   */
  const checkPendingPostTest = async () => {
    if (!auth.currentUser) {
      logDebug("No authenticated user, skipping pending post-test check");
      pendingPostTests.value = [];
      return;
    }

    try {
      isLoading.value = true;

      // ✅ STEP 1: Detect and re-create missing pending post-tests
      // This handles cases where post-test records were manually deleted
      logDebug("Detecting missing pending post-tests before loading queue");
      const recreatedCount = await pendingPostTestService.detectAndRecreateMissingPendingPostTests(
        auth.currentUser.uid
      );

      if (recreatedCount > 0) {
        logInfo(`Re-created ${recreatedCount} missing pending post-test(s)`, {
          count: recreatedCount,
        });
      }

      // ✅ STEP 2: Load the updated queue from Firestore
      const pending = await pendingPostTestService.getAllPendingPostTests(auth.currentUser.uid);

      if (pending.length > 0) {
        logInfo(`Found ${pending.length} pending post-test(s)`, {
          count: pending.length,
          hasMandatory: pending.some((t) => t.isMandatory),
          recreated: recreatedCount,
        });
      } else {
        logDebug("No pending post-tests found");
      }

      pendingPostTests.value = pending;
    } catch (error) {
      console.error("Error checking for pending post-tests:", error);
      pendingPostTests.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Clear specific pending post-test by sessionId (call after completion)
   */
  const clearPendingPostTestBySession = async (sessionId: string) => {
    try {
      await pendingPostTestService.clearPendingPostTest(sessionId);
      // Remove from local array
      pendingPostTests.value = pendingPostTests.value.filter(
        (test) => test.pendingPostTestId !== sessionId
      );
      logInfo("Pending post-test cleared locally", { sessionId });
    } catch (error) {
      console.error("Error clearing pending post-test:", error);
    }
  };

  /**
   * Clear all pending post-tests (use with caution)
   */
  const clearAllPendingPostTests = () => {
    pendingPostTests.value = [];
  };

  // Check on mount
  onMounted(() => {
    checkPendingPostTest();
  });

  return {
    // State
    pendingPostTests,
    isLoading,

    // Computed
    nextPendingPostTest,
    pendingCount,
    hasMandatory,
    hasBlockingPendingPostTest,

    // Methods
    checkPendingPostTest,
    clearPendingPostTestBySession,
    clearAllPendingPostTests,
  };
}
